import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { parsePesoToCentavos } from "@/lib/money";
import { scanClinicHealthReceipt, uploadClinicHealthReceiptImage } from "@/lib/apiClient";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "check", label: "Check" },
  { value: "card", label: "Card" },
  { value: "gcash", label: "GCash" },
  { value: "other", label: "Other" },
];

function todayIso() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const EMPTY_FORM = {
  categoryId: "",
  amount: "",
  expenseDate: "",
  vendorName: "",
  paymentMethod: "cash",
  branchId: "",
  description: "",
};

export default function AddExpenseDrawer({ open, onClose, categories, branches, currency, onSaved }) {
  const { activeClinicId } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({ ...EMPTY_FORM, expenseDate: todayIso() });
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const categoryList = Array.isArray(categories) ? categories : [];
  const branchList = Array.isArray(branches) ? branches : [];

  // Reset to a clean slate every time the drawer opens.
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, expenseDate: todayIso() });
      setFile(null);
      setScanning(false);
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const amountCentavos = useMemo(() => parsePesoToCentavos(form.amount), [form.amount]);
  const canSubmit = !!form.categoryId && amountCentavos > 0 && !submitting;

  const monthKeyFor = (dateValue) => {
    const iso = dateValue && dateValue.length >= 7 ? dateValue : todayIso();
    return iso.slice(0, 7);
  };

  const handleFileChange = async (event) => {
    const picked = event.target.files && event.target.files[0];
    if (!picked) {
      setFile(null);
      return;
    }
    setFile(picked);

    // Best-effort OCR prefill. Any failure falls back to manual entry and
    // never blocks the user from submitting.
    if (!activeClinicId) return;
    setScanning(true);
    try {
      const result = await scanClinicHealthReceipt({
        clinicId: activeClinicId,
        monthKey: monthKeyFor(form.expenseDate),
        file: picked,
      });
      if (result?.ok === false) {
        toast({
          title: "Couldn't read receipt",
          description: "Enter the details manually.",
          variant: "default",
        });
      } else {
        const scan = result?.scan || {};
        setForm((prev) => ({
          ...prev,
          vendorName: prev.vendorName || scan.merchant_name || "",
          amount:
            prev.amount ||
            (Number(scan.amount_centavos) > 0
              ? (Number(scan.amount_centavos) / 100).toFixed(2)
              : ""),
          expenseDate: prev.expenseDate || scan.receipt_date || prev.expenseDate,
        }));
        toast({
          title: "Receipt scanned",
          description: "We pre-filled what we could read. Double-check before saving.",
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't read receipt",
        description: "Enter the details manually.",
        variant: "default",
      });
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      let receiptImageUrl = null;
      let receiptStoragePath = null;

      // If a receipt photo is staged, upload it first so we can persist the
      // path/url on the expense row.
      if (file && activeClinicId) {
        let uploaded;
        try {
          uploaded = await uploadClinicHealthReceiptImage({
            clinicId: activeClinicId,
            monthKey: monthKeyFor(form.expenseDate),
            file,
          });
        } catch (uploadErr) {
          toast({
            title: "Receipt upload failed",
            description: uploadErr?.message || "Could not attach the receipt. Try again or remove the photo.",
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        if (uploaded?.ok === false) {
          toast({
            title: "Receipt upload failed",
            description: uploaded.message || "Could not attach the receipt. Try again or remove the photo.",
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        // Backend returns { bucket, storage_path, signed_url, expires_in }.
        receiptImageUrl = uploaded?.signed_url || null;
        receiptStoragePath = uploaded?.storage_path || null;
      }

      const payload = {
        category_id: form.categoryId,
        amount_centavos: amountCentavos,
        expense_date: form.expenseDate || todayIso(),
        vendor_name: form.vendorName.trim() || null,
        description: form.description.trim() || null,
        payment_method: form.paymentMethod,
        branch_id: form.branchId || null,
        receipt_image_url: receiptImageUrl,
        receipt_storage_path: receiptStoragePath,
      };

      const { error: rpcError } = await supabase.rpc("record_clinic_expense", {
        p_input: payload,
      });

      if (rpcError) {
        toast({
          title: "Couldn't save the expense",
          description: rpcError.message || "Something went wrong. Try again.",
          variant: "destructive",
        });
        setSubmitting(false);
        return;
      }

      toast({ title: "Expense recorded", description: "Your totals have been updated." });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Couldn't save the expense",
        description: err?.message || "Something went wrong. Try again.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  const inputClass =
    "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-[14px] text-slate-950 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100";

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next && !submitting) onClose?.(); }}>
      <SheetContent side="right" className="flex w-full max-w-[440px] flex-col gap-0 p-0 sm:max-w-[440px]">
        <SheetHeader className="border-b border-slate-200 px-5 py-4 text-left">
          <SheetTitle>Add expense</SheetTitle>
          <SheetDescription>Record a clinic cost. It posts to your ledger immediately.</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <Field label="Category" required>
              <select
                value={form.categoryId}
                onChange={(event) => setField("categoryId", event.target.value)}
                className={inputClass}
                required
              >
                <option value="">Select a category…</option>
                {categoryList.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Amount" required>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-slate-400">
                  ₱
                </span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(event) => setField("amount", event.target.value)}
                  placeholder="0.00"
                  className="h-10 pl-7"
                />
              </div>
            </Field>

            <Field label="Date">
              <Input
                type="date"
                value={form.expenseDate}
                onChange={(event) => setField("expenseDate", event.target.value)}
                className="h-10"
              />
            </Field>

            <Field label="Vendor">
              <Input
                type="text"
                value={form.vendorName}
                onChange={(event) => setField("vendorName", event.target.value)}
                placeholder="Who was paid"
                className="h-10"
              />
            </Field>

            <Field label="Payment method">
              <select
                value={form.paymentMethod}
                onChange={(event) => setField("paymentMethod", event.target.value)}
                className={inputClass}
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </Field>

            {branchList.length > 0 ? (
              <Field label="Branch">
                <select
                  value={form.branchId}
                  onChange={(event) => setField("branchId", event.target.value)}
                  className={inputClass}
                >
                  <option value="">Clinic-wide</option>
                  {branchList.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}

            <Field label="Note">
              <Textarea
                value={form.description}
                onChange={(event) => setField("description", event.target.value)}
                placeholder="Optional details"
                rows={2}
              />
            </Field>

            <Field label="Receipt photo">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                className="block w-full text-[13px] text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-[13px] file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
              />
              {scanning ? (
                <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.25} />
                  Reading receipt…
                </p>
              ) : null}
            </Field>
          </div>

          <SheetFooter className="border-t border-slate-200 px-5 py-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? "Saving…" : "Save expense"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[12px] font-semibold text-slate-600">
        {label}
        {required ? <span className="ml-0.5 text-rose-500">*</span> : null}
      </label>
      {children}
    </div>
  );
}
